import { useEffect, useRef, useState } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  api,
  type CommercialInvoice,
  type CommitCommercialInvoiceResponse,
  type GenerateCommercialInvoiceResponse,
  type ProformaInvoice,
  type Shipment,
} from '@/lib/api';
import { useModalDismiss } from '@/lib/use-modal-dismiss';

interface Props {
  open: boolean;
  onClose: () => void;
  proforma: ProformaInvoice;
  shipment: Shipment;
  rfqId?: string;
}

const CURRENCY_SYMBOL: Record<string, string> = {
  USD: '$', EUR: '€', GBP: '£', CAD: 'CA$', AUD: 'A$',
  JPY: '¥', CHF: 'CHF ', AED: 'AED ', SGD: 'S$',
};

function fmtMoney(cents: number, currency: string): string {
  const sym = CURRENCY_SYMBOL[currency] ?? `${currency} `;
  if (currency === 'JPY') return `${sym}${Math.round(cents / 100).toLocaleString('en-US')}`;
  return `${sym}${(cents / 100).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

export function CommercialInvoiceModal({ open, onClose, proforma, shipment, rfqId }: Props) {
  const dismiss = useModalDismiss(onClose);
  const { getToken } = useAuth();
  const qc = useQueryClient();

  const [htsCode, setHtsCode] = useState('');
  const [eccn, setEccn] = useState('EAR99');
  const [countryOfOrigin, setCountryOfOrigin] = useState('');
  const [portOfExport, setPortOfExport] = useState('');
  const [portOfEntry, setPortOfEntry] = useState('');
  const [endUse, setEndUse] = useState('');
  const [endUser, setEndUser] = useState('');
  const [licenseCitation, setLicenseCitation] = useState('NLR');
  const [soldToAddress, setSoldToAddress] = useState('');

  const [preview, setPreview] = useState<GenerateCommercialInvoiceResponse | null>(null);
  const [saved, setSaved] = useState<CommercialInvoice | null>(null);
  const iframeRef = useRef<HTMLIFrameElement | null>(null);

  useEffect(() => {
    if (!open) {
      setHtsCode('');
      setEccn('EAR99');
      setCountryOfOrigin('');
      setPortOfExport('');
      setPortOfEntry('');
      setEndUse('');
      setEndUser('');
      setLicenseCitation('NLR');
      setSoldToAddress('');
      setPreview(null);
      setSaved(null);
    }
  }, [open]);

  function invalidateLists() {
    qc.invalidateQueries({ queryKey: ['commercial-invoices'] });
    qc.invalidateQueries({ queryKey: ['shipments'] });
    if (rfqId) qc.invalidateQueries({ queryKey: ['rfq', rfqId] });
    qc.invalidateQueries({ queryKey: ['rfqs'] });
  }

  function buildBody() {
    const body: Record<string, unknown> = {
      proformaInvoicePublicId: proforma.id,
      shipmentPublicId: shipment.id,
    };
    if (htsCode) body.htsCode = htsCode;
    if (eccn) body.eccn = eccn;
    if (countryOfOrigin) body.countryOfOrigin = countryOfOrigin;
    if (portOfExport) body.portOfExport = portOfExport;
    if (portOfEntry) body.portOfEntry = portOfEntry;
    if (endUse) body.endUse = endUse;
    if (endUser) body.endUser = endUser;
    if (licenseCitation) body.licenseCitation = licenseCitation;
    if (soldToAddress) body.soldToAddress = soldToAddress;
    return body;
  }

  const previewMutation = useMutation({
    mutationFn: async () => {
      return api<GenerateCommercialInvoiceResponse>('/api/commercial-invoices/generate', {
        method: 'POST',
        tokenGetter: () => getToken(),
        body: JSON.stringify(buildBody()),
      });
    },
    onSuccess: (data) => {
      setPreview(data);
      setSaved(null);
    },
  });

  const saveMutation = useMutation({
    mutationFn: async () => {
      return api<CommitCommercialInvoiceResponse>('/api/commercial-invoices', {
        method: 'POST',
        tokenGetter: () => getToken(),
        idempotencyKey: crypto.randomUUID(),
        body: JSON.stringify(buildBody()),
      });
    },
    onSuccess: (data) => {
      setSaved(data.commercialInvoice);
      if (data.commercialInvoice.html) {
        setPreview({
          ciRef: data.commercialInvoice.ref,
          proformaRef: data.commercialInvoice.proformaRef,
          quoteRef: data.commercialInvoice.quoteRef,
          rfqRef: data.commercialInvoice.rfqRef,
          shipmentRef: data.commercialInvoice.shipmentRef,
          generatedAt: data.commercialInvoice.generatedAt,
          currency: data.commercialInvoice.currency,
          qty: data.commercialInvoice.qty,
          unitPriceCents: data.commercialInvoice.unitPriceCents,
          totalCents: data.commercialInvoice.totalCents,
          html: data.commercialInvoice.html,
        });
      }
      invalidateLists();
    },
  });

  useEffect(() => {
    if (!preview?.html || !iframeRef.current) return;
    const doc = iframeRef.current.contentDocument;
    if (!doc) return;
    doc.open();
    doc.write(preview.html);
    doc.close();
  }, [preview]);

  const downloadHtml = () => {
    if (!preview) return;
    const blob = new Blob([preview.html], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${preview.ciRef}.html`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const copyHtml = async () => {
    if (!preview) return;
    try {
      await navigator.clipboard.writeText(preview.html);
    } catch {
      window.prompt('Copy the HTML manually:', preview.html.slice(0, 100) + '…');
    }
  };

  const openInNewTab = () => {
    if (!preview) return;
    const win = window.open('', '_blank');
    if (!win) return;
    win.document.open();
    win.document.write(preview.html);
    win.document.close();
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4"
      {...dismiss}
    >
      <div className="bg-background border border-border rounded-lg shadow-xl w-full max-w-5xl max-h-[94vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b border-border">
          <div>
            <h2 className="font-semibold">Issue commercial invoice</h2>
            <p className="text-xs text-muted-foreground mt-0.5">
              Customs paperwork that ships with the goods. Pulls compliance fields from the vendor quote + shipment;
              broker can override below.
            </p>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground text-sm" aria-label="Close">✕</button>
        </div>

        <div className="flex-1 overflow-y-auto lg:overflow-hidden grid grid-cols-1 lg:grid-cols-[340px_1fr]">
          <aside className="border-b lg:border-r border-border overflow-auto p-5 space-y-4 bg-muted/20">
            <div>
              <div className="text-xs uppercase tracking-wide text-muted-foreground mb-2">Source</div>
              <div className="text-sm font-mono">Proforma {proforma.ref}</div>
              <div className="text-sm font-mono">Shipment {shipment.ref}</div>
              <div className="mt-2 font-mono text-sm font-semibold text-primary">
                {fmtMoney(proforma.totalCents, proforma.currency)}
                {proforma.qty > 1 && (
                  <span className="text-muted-foreground font-normal"> ({proforma.qty} ea)</span>
                )}
              </div>
            </div>

            {!saved && (
              <div className="space-y-3">
                <label className="block">
                  <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">Sold-to / Buyer address</div>
                  <textarea
                    value={soldToAddress}
                    onChange={(e) => setSoldToAddress(e.target.value)}
                    placeholder={'Full buyer postal address\ne.g. Thales SA\nRue des Frères Taymans 28\n1480 Tubize, Belgium'}
                    rows={3}
                    maxLength={500}
                    className="w-full px-2 py-1.5 text-sm bg-background border border-border rounded"
                  />
                </label>
                <label className="block">
                  <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">HTS code</div>
                  <input
                    value={htsCode}
                    onChange={(e) => setHtsCode(e.target.value)}
                    placeholder="auto from vendor quote"
                    maxLength={40}
                    className="w-full px-2 py-1.5 text-sm bg-background border border-border rounded font-mono"
                  />
                </label>
                <label className="block">
                  <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">ECCN</div>
                  <input
                    value={eccn}
                    onChange={(e) => setEccn(e.target.value)}
                    maxLength={40}
                    className="w-full px-2 py-1.5 text-sm bg-background border border-border rounded font-mono"
                  />
                </label>
                <label className="block">
                  <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">Country of origin</div>
                  <input
                    value={countryOfOrigin}
                    onChange={(e) => setCountryOfOrigin(e.target.value)}
                    placeholder="US, FR, GB, ..."
                    maxLength={80}
                    className="w-full px-2 py-1.5 text-sm bg-background border border-border rounded font-mono"
                  />
                </label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <label className="block">
                    <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">Port of export</div>
                    <input
                      value={portOfExport}
                      onChange={(e) => setPortOfExport(e.target.value)}
                      placeholder="JFK"
                      maxLength={120}
                      className="w-full px-2 py-1.5 text-sm bg-background border border-border rounded font-mono"
                    />
                  </label>
                  <label className="block">
                    <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">Port of entry</div>
                    <input
                      value={portOfEntry}
                      onChange={(e) => setPortOfEntry(e.target.value)}
                      placeholder="LHR"
                      maxLength={120}
                      className="w-full px-2 py-1.5 text-sm bg-background border border-border rounded font-mono"
                    />
                  </label>
                </div>
                <label className="block">
                  <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">License citation</div>
                  <input
                    value={licenseCitation}
                    onChange={(e) => setLicenseCitation(e.target.value)}
                    placeholder="NLR / EAR99 / 9A991"
                    maxLength={40}
                    className="w-full px-2 py-1.5 text-sm bg-background border border-border rounded font-mono"
                  />
                </label>
                <label className="block">
                  <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">End-user</div>
                  <input
                    value={endUser}
                    onChange={(e) => setEndUser(e.target.value)}
                    placeholder="Ultimate consignee"
                    maxLength={200}
                    className="w-full px-2 py-1.5 text-sm bg-background border border-border rounded"
                  />
                </label>
                <label className="block">
                  <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">End-use</div>
                  <textarea
                    value={endUse}
                    onChange={(e) => setEndUse(e.target.value)}
                    placeholder="What the part is being used for"
                    rows={2}
                    maxLength={500}
                    className="w-full px-2 py-1.5 text-sm bg-background border border-border rounded"
                  />
                </label>
              </div>
            )}

            {!preview ? (
              <button
                onClick={() => previewMutation.mutate()}
                disabled={previewMutation.isPending}
                className="w-full px-3 py-2 text-sm font-medium bg-primary text-primary-foreground rounded-md disabled:opacity-50"
              >
                {previewMutation.isPending ? 'Generating…' : 'Generate preview'}
              </button>
            ) : !saved ? (
              <div className="space-y-2">
                <div className="border border-border rounded p-3 bg-background">
                  <div className="text-xs uppercase tracking-wide text-muted-foreground mb-1">Preview</div>
                  <div className="text-sm font-mono">{preview.ciRef}</div>
                  <div className="text-[11px] text-muted-foreground mt-1.5 italic">Not issued yet.</div>
                </div>
                <button
                  onClick={() => saveMutation.mutate()}
                  disabled={saveMutation.isPending}
                  className="w-full px-3 py-2 text-sm font-medium bg-primary text-primary-foreground rounded-md disabled:opacity-50"
                >
                  {saveMutation.isPending ? 'Issuing…' : 'Issue commercial invoice'}
                </button>
                <button onClick={downloadHtml} className="w-full px-3 py-2 text-sm border border-border rounded-md hover:bg-muted">Download preview .html</button>
                <button onClick={copyHtml} className="w-full px-3 py-2 text-sm border border-border rounded-md hover:bg-muted">Copy preview HTML</button>
                <button onClick={openInNewTab} className="w-full px-3 py-2 text-sm border border-border rounded-md hover:bg-muted">Open in new tab</button>
                <button
                  onClick={() => setPreview(null)}
                  className="w-full px-3 py-2 text-xs text-muted-foreground hover:text-foreground"
                >
                  ← Adjust &amp; regenerate
                </button>
              </div>
            ) : (
              <div className="space-y-2">
                <div className="border border-border rounded p-3 bg-background">
                  <div className="text-xs uppercase tracking-wide text-muted-foreground mb-1">
                    Issued · <span className="font-semibold text-emerald-600">{saved.status}</span>
                  </div>
                  <div className="text-sm font-mono">{saved.ref}</div>
                  {saved.issuedAt && (
                    <div className="text-[11px] text-muted-foreground mt-1">{new Date(saved.issuedAt).toLocaleString()}</div>
                  )}
                </div>
                <div className="border-t border-border pt-2 mt-2 space-y-2">
                  <button onClick={downloadHtml} className="w-full px-3 py-2 text-sm border border-border rounded-md hover:bg-muted">Download .html</button>
                  <button onClick={copyHtml} className="w-full px-3 py-2 text-sm border border-border rounded-md hover:bg-muted">Copy HTML</button>
                  <button onClick={openInNewTab} className="w-full px-3 py-2 text-sm border border-border rounded-md hover:bg-muted">Open in new tab</button>
                </div>
              </div>
            )}

            {previewMutation.isError && (
              <div className="text-sm text-destructive">{(previewMutation.error as { message?: string })?.message ?? 'Generation failed'}</div>
            )}
            {saveMutation.isError && (
              <div className="text-sm text-destructive">{(saveMutation.error as { message?: string })?.message ?? 'Issue failed'}</div>
            )}
          </aside>

          <section className="overflow-hidden bg-[#f4f6fa] flex items-stretch min-h-[55vh] lg:min-h-0">
            {preview ? (
              <iframe ref={iframeRef} title="Commercial invoice preview" className="w-full h-full border-0 bg-white" />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-sm text-muted-foreground">
                Set fields on the left and click <span className="mx-1 font-medium">Generate preview</span>.
              </div>
            )}
          </section>
        </div>

        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-border">
          <button onClick={onClose} className="px-3 py-1.5 text-sm rounded-md text-muted-foreground hover:bg-muted">Done</button>
        </div>
      </div>
    </div>
  );
}
